import React, { useCallback, useState } from 'react';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { LicenseScreen } from 'app/auth/LicenseScreen';

/** Same key StorageConfiguratorApp has always used (App.js handleLicenseAccept / handleSignOut). */
export const LICENSE_STORAGE_KEY = 'licenseAccepted';

export function readLicenseAccepted(): boolean {
  try {
    return localStorage.getItem(LICENSE_STORAGE_KEY) === 'true';
  } catch {
    return sessionStorage.getItem(LICENSE_STORAGE_KEY) === 'true';
  }
}

export function writeLicenseAccepted(): void {
  try {
    localStorage.setItem(LICENSE_STORAGE_KEY, 'true');
  } catch {
    sessionStorage.setItem(LICENSE_STORAGE_KEY, 'true');
  }
}

/**
 * Clears the acceptance so the next user on this browser sees the wall again.
 *
 * Deliberately clears BOTH stores rather than mirroring the read/write helpers'
 * try/localStorage-catch/sessionStorage shape (which is also what App.js's
 * handleSignOut does): those two only ever need to reach the store the *write*
 * landed in, but a stale key in the other store is a licence-acceptance hole,
 * and a browser can move between the two across sessions (private window,
 * blocked site data). Each removal is guarded separately so a throwing store
 * cannot stop the other from being cleared.
 */
export function clearLicenseAccepted(): void {
  try {
    localStorage.removeItem(LICENSE_STORAGE_KEY);
  } catch {
    /* storage unavailable — nothing to clear there */
  }
  try {
    sessionStorage.removeItem(LICENSE_STORAGE_KEY);
  } catch {
    /* storage unavailable — nothing to clear there */
  }
}

type Props = { user: unknown; children: React.ReactNode };

/**
 * Route-level license wall. Mirrors the condition StorageConfiguratorApp used
 * inline (App.js:1850) so the classic route behaves exactly as before, and
 * gives /v2 the same wall. UX only — server-side auth is the model @auth rules.
 * Note: Phase 0b removed the inline license branch from StorageConfiguratorApp,
 * but StorageConfiguratorApp still calls useUserPermissions(user) itself (for
 * everything else it derives from permissions), so / still issues two
 * fetchAuthSession calls per load — one here, one there. Unifying them is
 * deferred to Phase 1.
 */
export function LicenseGate({ user, children }: Props) {
  const { hasSpecialAccess, isCheckingAccess, licenseCheckComplete } = useUserPermissions(user);
  const [accepted, setAccepted] = useState<boolean>(readLicenseAccepted);
  const onAccept = useCallback(() => {
    writeLicenseAccepted();
    setAccepted(true);
  }, []);

  const mustAccept = licenseCheckComplete && !isCheckingAccess && !accepted && !hasSpecialAccess;
  if (mustAccept) return <LicenseScreen onAccept={onAccept} />;
  return <>{children}</>;
}

export default LicenseGate;
